import React, { useEffect, useRef } from 'react';
import { MapPin, ExternalLink } from 'lucide-react';
import api from '@/lib/api';

/**
 * ClinicMap — self-rendered Leaflet map.
 *
 * Why not iframe?
 *   - Google's key-less embed (`maps.google.com/maps?q=...&output=embed`) now
 *     gets blocked with "This content is blocked. Contact the site owner".
 *   - OSM's `export/embed.html` brings noisy "Make a Donation / contributors"
 *     attribution + lacks reliable auto-zoom.
 *   - Leaflet ships our own canvas: we control zoom (16 — neighbourhood-tight),
 *     marker styling, and attribution placement.
 *
 * Tile source: CartoDB Positron (free, no API key, OSM data, light theme that
 * matches DocSite). Falls back to vanilla OSM tiles if CartoDB blocks.
 *
 * SEO note: Map iframes / canvases aren't indexed; what helps local-SEO is the
 * rendered address text + JSON-LD `geo` lat/lon. Both are still in the page.
 */
export default function ClinicMap({
  locationId,
  name,
  address,
  pincode,
  height = 260,
}) {
  const mapEl = useRef(null);
  const mapRef = useRef(null);
  const [coords, setCoords] = React.useState(null);
  const [quality, setQuality] = React.useState('precise');
  const [err, setErr] = React.useState(null);

  const query = [name, address, pincode, 'India'].filter(Boolean).join(', ');
  const openInNewTab = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;

  // Fetch clinic lat/lon once (cached server-side after first call).
  // Two paths: with location_id (per-clinic, persisted on the location doc)
  // OR fallback to ad-hoc geocode-query for branding/preview callers.
  useEffect(() => {
    if (!query) return;
    let cancelled = false;
    const promise = locationId
      ? api.get(`/public/clinic-geo/${locationId}`)
      : api.get(`/public/geocode-query`, { params: { q: query } });
    promise
      .then(r => {
        if (cancelled) return;
        // Defensive: backend has been seen to return null / empty / malformed
        // payloads when geocoding fails. Without this guard, Leaflet's
        // L.map([undefined, undefined]) throws and React's error boundary
        // blanks the whole dashboard tab.
        const lat = Number(r?.data?.lat);
        const lon = Number(r?.data?.lon);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) {
          setErr('Map unavailable for this pincode');
          return;
        }
        setCoords({ lat, lon });
        setQuality(r?.data?.quality || 'precise');
      })
      .catch(() => { if (!cancelled) setErr('Map unavailable'); });
    return () => { cancelled = true; };
  }, [locationId, query]);

  // Lazy-init Leaflet once we have coords.
  useEffect(() => {
    if (!coords || !mapEl.current || mapRef.current) return;
    let cancelled = false;
    (async () => {
      const L = (await import('leaflet')).default;
      // Side-effect: leaflet css. Imported here so it tree-shakes off the
      // pages that don't render a map.
      await import('leaflet/dist/leaflet.css');
      if (cancelled || !mapEl.current) return;

      const map = L.map(mapEl.current, {
        center: [coords.lat, coords.lon],
        zoom: 16,
        scrollWheelZoom: false,        // patient won't accidentally hijack scroll
        attributionControl: false,     // we render a custom mini attribution below
        zoomControl: true,
      });
      mapRef.current = map;

      // CartoDB Positron — clean, light, OSM data; no API key required.
      L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
        maxZoom: 19,
        subdomains: 'abcd',
      }).addTo(map);

      // Marker — small div icon avoids Leaflet's default-marker-image issue
      // when bundlers don't copy the PNG asset.
      const icon = L.divIcon({
        className: 'docsite-pin',
        html: `<div style="width:22px;height:22px;border-radius:50%;background:#0EA5E9;border:3px solid white;box-shadow:0 2px 6px rgba(0,0,0,.35)"></div>`,
        iconSize: [22, 22],
        iconAnchor: [11, 11],
      });
      L.marker([coords.lat, coords.lon], { icon }).addTo(map);

      // Trigger a resize the moment leaflet attaches — fixes the grey-tile bug
      // when the container starts off hidden / not yet in layout.
      setTimeout(() => map.invalidateSize(), 60);
    })();
    return () => {
      cancelled = true;
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, [coords]);

  if (!query) return null;

  return (
    <div className="rounded-2xl overflow-hidden border border-slate-200 bg-slate-50" data-testid="clinic-map">
      <div
        ref={mapEl}
        style={{ width: '100%', height, background: '#f1f5f9' }}
        data-testid="clinic-map-canvas"
      >
        {!coords && !err && (
          <div className="w-full h-full flex items-center justify-center text-sm text-slate-400">
            Loading map…
          </div>
        )}
        {err && (
          <div className="w-full h-full flex items-center justify-center text-sm text-slate-500">
            {err} — <a href={openInNewTab} target="_blank" rel="noreferrer" className="text-sky-600 ml-1 font-semibold">open in Google Maps</a>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between gap-3 px-4 py-2.5 bg-white border-t border-slate-100">
        <p className="text-xs text-slate-600 flex items-center gap-1.5 truncate">
          <MapPin size={12} className="text-sky-500 flex-shrink-0" />
          <span className="truncate">{query}</span>
          {quality === 'approximate' && coords && (
            <span
              className="ml-1 inline-flex items-center rounded-full bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[10px] font-semibold text-amber-700"
              title="We couldn't pin the exact spot — showing the locality. Use 'Open in Google Maps' for turn-by-turn."
              data-testid="clinic-map-approximate-badge"
            >
              Approximate
            </span>
          )}
        </p>
        <a
          href={openInNewTab}
          target="_blank"
          rel="noopener noreferrer nofollow"
          className="text-xs text-sky-600 hover:text-sky-700 font-medium flex items-center gap-1 flex-shrink-0"
          data-testid="clinic-map-open-link"
        >
          Open in Google Maps <ExternalLink size={10} />
        </a>
      </div>
    </div>
  );
}
